import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, firstValueFrom, map, Observable, Subscription, switchMap } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, shareReplay, skip, take, tap } from 'rxjs/operators';
import { EmployeeEntity, EmployeeStatusEnum } from '../../../../dave-data-module/entities/employee.entity';
import { EntityTypesEnum } from '../../../../dave-data-module/entities/entity-role.entity';
import { UserToCustomerEntity } from '../../../../dave-data-module/entities/user-to-customer.entity';
import { EmployeeResolver } from '../../../../dave-data-module/guards/employee.resolver';
import { EntityRoleResolver } from '../../../../dave-data-module/guards/entity-role.resolver';
import { JobSpecificationResolver } from '../../../../dave-data-module/guards/job-specification.resolver';
import {
    UserToCustomerDataService,
} from '../../../../dave-data-module/services/user-to-customer-data.service';
import { State } from '../../../../dave-data-module/State';
import {
    getEmployees,
    getEmployeesActiveSortedByNameStartWithMe,
    getEmployeesFetched,
} from '../../../../dave-data-module/State/selectors/employees.selectors';
import {
    getEntityRole,
    getEntityRoleDictionary,
    getEntityRoleFetched,
} from '../../../../dave-data-module/State/selectors/entity-role.selector';
import {
    getJobSpecificationDictionary,
    getJobSpecificationFetched,
} from '../../../../dave-data-module/State/selectors/job-specification.selector';
import { isNotNullOrUndefined, SearchQueriesDebounceTime, stringSearch } from '../../../../helper/helper';
import { DefaultFilterService, FilterApps, FilterTypes } from '../../../../services/default-filter.service';
import { LoadingService } from '../../../../services/loading.service';
import {
    ISelectedElement,
    ITableConfig,
    TableContentType,
} from '../../multi-select-table/multi-select-table.component';
import { SelectUserDialogBase } from '../select-user-dialog-base';
import { FilterOption } from '../../../../dave-utils-module/app-filter-module/app-filter/app-filter.component';
import {
    BreakpointObserverService,
} from '../../../../dave-utils-module/dave-shared-components-module/services/breakpoint-observer.service';
import { CustomLabelService } from '../../../../services/custom-label/custom-label.service';
import { DepartmentResolver } from '../../../../dave-data-module/guards/department.resolver';
import { getFetched$ } from '../../../../dave-data-module/helper/helper';
import {
    getDepartmentDictionary,
    getDepartmentFetched,
} from '../../../../dave-data-module/State/selectors/department.selector';
import { PartnerOfficeResolver } from '../../../../dave-data-module/guards/partner-office.resolver';
import {
    getPartnerOfficeDictionary,
    getPartnerOfficesFetched,
} from '../../../../dave-data-module/State/selectors/partners.selectors';

export interface SelectUserCustomerDialogComponentDialogData {
    customerId: number;
}

interface ITableData {
    userId: number;
    jobSpecificationId: number;
    selectedForm: FormControl<boolean>;
    name: TableContentType<any> & { __typename: 'string' };
    jobSpecification: TableContentType<any> & { __typename: 'string' };
    role: TableContentType<{ id: number; label: string }> & { __typename: 'select' };
    employeeStaus: EmployeeStatusEnum | null;
    departmentId: number | null;
    partnerOffice: TableContentType<any> & { __typename: 'string' };
}

interface SelectedUserType extends ISelectedElement {
    userToCustomer: UserToCustomerEntity;
}

@Component({
    selector: 'app-select-user-customer-dialog',
    templateUrl: './select-user-customer-dialog.component.html',
    styleUrls: ['./select-user-customer-dialog.component.scss'],
})
export class SelectUserCustomerDialogComponent extends SelectUserDialogBase {
    public _FilterSettings$: Observable<FilterOption[]> = this.FilterSettings$.pipe(map(filters => filters.filter(f => f.Name !== FilterTypes.HideKollision)));
    public SearchString: BehaviorSubject<string> = new BehaviorSubject<string>('');

    SelectedUsers$: Observable<SelectedUserType[]>;
    TableDataConfig$ = combineLatest([
        this.BS.MobileQuery.pipe(distinctUntilChanged()),
        this.cls.getSingle$('Department'),
    ]).pipe(map(([mobile, departmentLabel]) => {
        return <ITableConfig[]>[
            {
                header: 'Name',
                id: 'name',
                sortAccessor: (cell: TableContentType<any> & { __typename: 'string' }) => cell.content || '',
            },
            {
                header: 'Jobbezeichnung',
                id: mobile ? null : 'jobSpecification',
                sortAccessor: (cell: TableContentType<any> & { __typename: 'string' }) => cell.content || '',
            },
            {
                header: departmentLabel,
                id: mobile ? null : 'department',
                sortAccessor: (cell: TableContentType<any> & { __typename: 'string' }) => cell.content || '',
            },
            {
                header: 'Unternehmen',
                id: mobile ? null : 'partnerOffice',
                sortAccessor: (cell: TableContentType<any>&{__typename: "string"}) => cell.content || '',
            },
            {
                header: 'Rolle',
                id: 'role',
                width: mobile ? '10em' : '20em',
            },
        ].filter(c => !!c.id);
    }));
    FilteredTableData$: Observable<ITableData[]>;
    TableData$: Observable<ITableData[]>;

    public ContentResolved$: Observable<boolean>;
    EntityRolesForCustomer$ = this.store.select(getEntityRoleFetched).pipe(
        filter((v) => !!v),
        switchMap(() => this.store.select(getEntityRole)),
        map((roles) => roles.filter((role) => role.Entity === EntityTypesEnum.Customer)),
        shareReplay({ refCount: true, bufferSize: 1 }),
    );

    constructor(
        private store: Store<State>,
        @Inject(MAT_DIALOG_DATA) public Dialogdata: SelectUserCustomerDialogComponentDialogData,
        private actions$: Actions,
        protected UserToCustomerService: UserToCustomerDataService,
        public LS: LoadingService,
        public BS: BreakpointObserverService,
        defaultFilter: DefaultFilterService,
        employeeRes: EmployeeResolver,
        jobRes: JobSpecificationResolver,
        entityRoleRes: EntityRoleResolver,
        cls: CustomLabelService,
        departmentResolver: DepartmentResolver,
        partnerOfficeResolver: PartnerOfficeResolver,
    ) {
        super(store, defaultFilter, FilterApps.SelectUserForCustomerPopup, departmentResolver, cls);
        // this._FilterSettings$ = this.FilterSettings$.pipe(map(filters => filters.filter(f => f.Name !== FilterTypes.HideKollision)));
        this.ContentResolved$ = combineLatest([
            this.store.select(getEmployeesFetched).pipe(
                distinctUntilChanged(),
                tap((fetched) => {
                    if (!fetched) {
                        employeeRes.resolve();
                    }
                }),
            ),
            this.store.select(getJobSpecificationFetched).pipe(
                distinctUntilChanged(),
                tap((fetched) => {
                    if (!fetched) {
                        jobRes.resolve();
                    }
                }),
            ),
            // this.store.select(getUserToCustomersFetched).pipe(
            //     distinctUntilChanged(),
            //     tap((fetched) => {
            //         if (!fetched) {
            //             userToCustomerRes.resolve();
            //         }
            //     }),
            // ),
            this.store.select(getEntityRoleFetched).pipe(
                distinctUntilChanged(),
                tap((fetched) => {
                    if (!fetched) {
                        entityRoleRes.resolve();
                    }
                }),
            ),
            this.store.select(getDepartmentFetched).pipe(
                distinctUntilChanged(),
                tap((fetched) => {
                    if (!fetched) {
                        departmentResolver.resolve();
                    }
                }),
            ),
            this.store.select(getPartnerOfficesFetched).pipe(
                distinctUntilChanged(),
                tap((fetched) => {
                    if (!fetched) {
                        partnerOfficeResolver.resolve();
                    }
                }),
            ),
        ]).pipe(
            map((values) => values.every((v) => v)),
            distinctUntilChanged(),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );

        const employeesWithUserId$ = this.store.select(getEmployeesFetched).pipe(
            filter((v) => !!v),
            switchMap(() => this.store.select(getEmployees)),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );
        /* const activeEmployeesWithUserId$ = employeesWithUserId$.pipe(map(employees => employees.filter(e => e.EmployeeStatus === EmployeeStatusEnum.Aktiv && !e.Deleted))) */
        this.SelectedUsers$ = combineLatest([
            this.UserToCustomerService.GetFromCustomer$(this.Dialogdata.customerId),
            this.store.select(getEntityRoleFetched).pipe(
                filter((v) => !!v),
                switchMap(() => this.store.select(getEntityRoleDictionary)),
            ),
            employeesWithUserId$.pipe(map((employees) => new Map<number, EmployeeEntity>(employees.map((e) => [e.UserId, e])))),
        ]).pipe(
            map(([u2c, roles, employeeMap]) => [
                ...u2c
                    .filter((u) => u.CustomerId === this.Dialogdata.customerId)
                    .map((u) => ({
                        userToCustomer: u,
                        label: (employeeMap.get(u.UserId)?.DisplayName || '') + (u.RoleId ? ' (' + (roles[u.RoleId]?.DisplayName || '') + ')' : ''),
                    })),
            ]),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );
        this.TableData$ = combineLatest([
            /* activeEmployeesWithUserId$, */
            this.store.select(getEmployeesActiveSortedByNameStartWithMe),
            this.store.select(getJobSpecificationFetched).pipe(
                filter((v) => !!v),
                switchMap(() => this.store.select(getJobSpecificationDictionary)),
            ),
            this.EntityRolesForCustomer$.pipe(map((roles) => roles.map((role) => ({
                id: role.Id,
                label: role.DisplayName,
            })))),
            getFetched$(this.store, getDepartmentFetched, getDepartmentDictionary),
            getFetched$(this.store, getPartnerOfficesFetched, getPartnerOfficeDictionary),
        ]).pipe(
            map(([employees, jobs, entityRoleOptions, departments, partnerOffices]) => {
                return employees
                    .filter((e) => e.UserId)
                    .map<ITableData>((e) => {
                        const job = e.JobSpecificationId && jobs[e.JobSpecificationId];
                        const department = e.DepartmentId && departments[e.DepartmentId];
                        const partnerOffice = e.PartnerOfficeId && partnerOffices[e.PartnerOfficeId];
                        return {
                            userId: e.UserId,
                            selectedForm: new FormControl<boolean>(false),
                            jobSpecificationId: e.JobSpecificationId,
                            employeeStaus: e.EmployeeStatus,
                            name: {
                                __typename: 'string',
                                content: e.DisplayName,
                            },
                            jobSpecification: {
                                __typename: 'string',
                                content: job?.Name || '',
                            },
                            department: {
                                __typename: 'string',
                                content: department?.Name || '',
                            },
                            partnerOffice: {
                                __typename: 'string',
                                content: partnerOffice?.DisplayName || '',
                            },
                            role: {
                                __typename: 'select',
                                options: entityRoleOptions,
                                optionLabel: (o) => o.label,
                                formControl: new FormControl<number | null>(
                                    job ? entityRoleOptions.find((er) => er.label === job.Name.trim())?.id || entityRoleOptions[0]?.id : entityRoleOptions[0]?.id,
                                    Validators.required,
                                ),
                            },
                            departmentId: e.DepartmentId,
                        };
                    });
            }),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );
        this.FilteredTableData$ = this.TableData$.pipe(
            switchMap((rows) =>
                this.FilterValues$.pipe(
                    map((filter) =>
                        filter[FilterTypes.JobSpecificationIds]?.length || filter[FilterTypes.Status]?.length || filter[FilterTypes.Departments]?.length ?
                            rows.filter((r) => (
                                    (!filter[FilterTypes.JobSpecificationIds]?.length || r.jobSpecificationId && filter[FilterTypes.JobSpecificationIds].includes(r.jobSpecificationId + ''))
                                    && (!filter[FilterTypes.Status]?.length || filter[FilterTypes.Status].some(f => f.id === r.employeeStaus))
                                    && (!filter[FilterTypes.Departments]?.length || filter[FilterTypes.Departments].some(f => f.id === r.departmentId))
                                ),
                            )
                            : rows,
                    ),
                ),
            ),
            switchMap((rows) =>
                this.SearchString.pipe(
                    debounceTime(SearchQueriesDebounceTime),
                    map((searchString) => (!searchString && rows) || rows.filter((r) => [r.name.content, r.jobSpecification.content].filter(isNotNullOrUndefined).some(s => stringSearch(s, searchString)))),
                ),
            ),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );
    }

    RemoveSelected(entity: SelectedUserType) {
        this.LS.startLoading('delete-user-to-customer');
        this.UserToCustomerService.Remove(entity.userToCustomer.Id).then(() => {
            this.LS.endLoading('delete-user-to-customer');
        });
    }

    Submit() {
        this.LS.startLoading('add-users-to-customer');
        firstValueFrom(this.TableData$).then((data) => {
            let promises: Promise<UserToCustomerEntity[]>[] = [];
            data.filter((d) => d.selectedForm.value).forEach((d) => {
                promises.push(this.UserToCustomerService.Add({
                    userId: d.userId,
                    customerId: this.Dialogdata.customerId,
                    roleId: +d.role.formControl.value || undefined,
                }));
            });
            if (promises.length != 0) {
                combineLatest(promises).pipe(take(1)).subscribe(() => {
                    data.forEach(d => {
                        if (d.selectedForm.value) {
                            d.selectedForm.reset();
                        }
                        this.LS.endLoading('add-users-to-customer');
                    });
                });
            } else {
                this.LS.endLoading('add-users-to-customer');
            }
        });
    }
}
